// CfoInsightsShell.jsx
// Shared CFO-Insights page shell: top bar + left nav (desktop) + mobile tab strip.
// Both CfoInsightsDashboard and ScenariosPage render inside this shell so the
// global navigation is defined in exactly one place.
//
// Dashboard tabs (Executive Summary, Cash Flow, …) are parent-driven:
//   - On the dashboard route, the parent passes its local tab state and setter.
//   - On the Scenarios route, the parent passes `activeTabId={null}` and an
//     onTabClick that navigates to /cfo-insights?tab=<id>. The dashboard reads
//     that query param on mount to restore the selected tab.
//
// The "Scenario Modeling" entry is a route, not a tab: the shell owns its
// navigation to /cfo-insights/scenarios and its active state (based on the
// current pathname).

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';

// Mirrors CfoInsightsDashboard's own `tabs` array exactly (including the
// `readiness` id) so a tab clicked here via `?tab=<id>` always matches a tab
// CfoInsightsDashboard actually knows how to render.
export const CFO_TABS = [
  { id: 'summary', label: 'Executive Summary' },
  { id: 'cashflow', label: 'Cash Flow' },
  { id: 'margin', label: 'Margin & COS' },
  { id: 'arap', label: 'AR / AP Risk' },
  { id: 'bankability', label: 'Bankability' },
  { id: 'capitalstack', label: 'CapitalStack' },
  { id: 'aging', label: 'Aging Detail' },
  { id: 'readiness', label: 'Readiness' },
];

const SCENARIOS_PATH = '/cfo-insights/scenarios';
const BONDREADY_PATH = '/cfo-insights/bondready';

export default function CfoInsightsShell({
  children = null,
  orgDisplayName = '',
  orgShortName = '',
  activeTabId = null,
  onTabClick = null,
  fullWidth = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const onScenariosRoute = location.pathname === SCENARIOS_PATH;
  const onBondReadyRoute = location.pathname === BONDREADY_PATH
    || location.pathname.startsWith(`${BONDREADY_PATH}/`);
  const onRouteNav = onScenariosRoute || onBondReadyRoute;
  const avatarLetter = (orgShortName || 'C').charAt(0).toUpperCase();

  const handleTabClick = (id) => {
    if (typeof onTabClick === 'function') {
      onTabClick(id);
    } else {
      // Fallback: route to the dashboard with the requested tab preselected.
      navigate(`/cfo-insights?tab=${id}`);
    }
  };

  const handleScenariosClick = () => {
    if (!onScenariosRoute) navigate(SCENARIOS_PATH);
  };

  const handleBondReadyClick = () => {
    if (!onBondReadyRoute) navigate(BONDREADY_PATH);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Top Bar */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-20">
        <div className={fullWidth ? 'flex w-full items-center justify-between px-4 py-3 lg:px-7' : 'mx-auto flex max-w-6xl items-center justify-between px-4 py-3'}>
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/30">
              <span className="text-lg font-bold text-sky-300">{avatarLetter}</span>
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-sky-400 truncate">
                {orgDisplayName}
              </div>
              <div className="hidden sm:block text-sm font-semibold text-slate-100">
                CFO-Insights powered by ConstructPay
              </div>
              <div className="sm:hidden text-sm font-semibold text-slate-100">
                CFO-Insights
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline-flex rounded-full bg-emerald-500/10 px-2 py-1 text-emerald-300 text-xs">
              Sub View
            </span>
            <span className="hidden md:inline-flex rounded-full bg-slate-800 px-2 py-1 text-slate-300 text-xs">
              GC View
            </span>
            <span className="hidden md:inline-flex rounded-full bg-slate-800 px-2 py-1 text-slate-300 text-xs">
              Lender View
            </span>
            <button
              type="button"
              onClick={() => auth.signOut()}
              className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors min-h-[44px]"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Layout */}
      <main className={fullWidth ? 'flex flex-1 min-h-0 overflow-hidden' : 'mx-auto max-w-6xl px-4 py-4 lg:flex lg:gap-4'}>
        {/* Left Nav — desktop only */}
        <aside className={fullWidth
          ? 'hidden lg:flex w-[230px] shrink-0 flex-col border-r border-[#1e2d4a] bg-[#111a2e] py-[18px] overflow-y-auto'
          : 'hidden lg:block w-56 shrink-0 rounded-xl border border-slate-800 bg-slate-900/60 p-2 self-start'}>
          <div className="mb-2 px-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Dashboard
          </div>
          <nav className="space-y-1 text-sm" aria-label="CFO Insights">
            {CFO_TABS.map((tab) => {
              const isActive = !onRouteNav && activeTabId === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabClick(tab.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex w-full min-h-[44px] items-center justify-between rounded-lg px-3 py-2 text-left transition ${
                    isActive
                      ? 'bg-sky-600 text-white shadow-sm'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <span>{tab.label}</span>
                  {isActive && (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-sky-100">
                      Active
                    </span>
                  )}
                </button>
              );
            })}

            <div className="my-2 h-px bg-slate-800" aria-hidden="true" />

            <button
              type="button"
              onClick={handleBondReadyClick}
              aria-current={onBondReadyRoute ? 'page' : undefined}
              data-testid="cfo-nav-bondready"
              className={`flex w-full min-h-[44px] items-center justify-between rounded-lg px-3 py-2 text-left transition ${
                onBondReadyRoute
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              <span>BondReady</span>
              {onBondReadyRoute ? (
                <span className="text-[10px] uppercase tracking-[0.2em] text-sky-100">
                  Active
                </span>
              ) : null}
            </button>

            <button
              type="button"
              onClick={handleScenariosClick}
              aria-current={onScenariosRoute ? 'page' : undefined}
              data-testid="cfo-nav-scenarios"
              className={`flex w-full min-h-[44px] items-center justify-between rounded-lg px-3 py-2 text-left transition ${
                onScenariosRoute
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              <span>Scenario Modeling</span>
              {onScenariosRoute ? (
                <span className="text-[10px] uppercase tracking-[0.2em] text-sky-100">
                  Active
                </span>
              ) : (
                <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.15em] text-amber-300 border border-amber-400/30">
                  New
                </span>
              )}
            </button>
          </nav>
        </aside>

        {/* Right Content */}
        <section className={fullWidth ? 'flex-1 min-w-0 overflow-y-auto px-4 py-4 lg:px-7 lg:py-6 lg:pb-14' : 'flex-1 min-w-0 space-y-4'}>
          {/* Mobile tab strip — hidden on lg+ */}
          <nav
            className="lg:hidden -mx-4 px-4 overflow-x-auto"
            style={{ WebkitOverflowScrolling: 'touch' }}
            aria-label="CFO Insights (mobile)"
          >
            <div className="flex gap-1.5 pb-1">
              {CFO_TABS.map((tab) => {
                const isActive = !onRouteNav && activeTabId === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabClick(tab.id)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`shrink-0 rounded-lg px-3 py-2.5 text-xs font-medium whitespace-nowrap transition min-h-[44px] ${
                      isActive
                        ? 'bg-sky-600 text-white shadow-sm'
                        : 'bg-slate-800/80 text-slate-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={handleBondReadyClick}
                aria-current={onBondReadyRoute ? 'page' : undefined}
                data-testid="cfo-nav-bondready-mobile"
                className={`shrink-0 rounded-lg px-3 py-2.5 text-xs font-medium whitespace-nowrap transition min-h-[44px] ${
                  onBondReadyRoute
                    ? 'bg-sky-600 text-white shadow-sm'
                    : 'bg-slate-800/80 text-slate-300'
                }`}
              >
                BondReady
              </button>
              <button
                type="button"
                onClick={handleScenariosClick}
                aria-current={onScenariosRoute ? 'page' : undefined}
                data-testid="cfo-nav-scenarios-mobile"
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium whitespace-nowrap transition min-h-[44px] ${
                  onScenariosRoute
                    ? 'bg-sky-600 text-white shadow-sm'
                    : 'bg-slate-800/80 text-slate-300'
                }`}
              >
                <span>Scenario Modeling</span>
                {!onScenariosRoute && (
                  <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.15em] text-amber-300 border border-amber-400/30">
                    New
                  </span>
                )}
              </button>
            </div>
          </nav>

          {children}
        </section>
      </main>
    </div>
  );
}
